import { mobileShopApi } from '../mobileShopApi';
import { Brand, MasterRow, PagedResponse, PhoneModel } from '../../types/master';

/**
 * One set of endpoints for every master, keyed by path - the same idea as
 * ConfigurationApi in the retail app. Each master exposes the same six routes,
 * so the screen only has to pass its path.
 */
export const masterApi = mobileShopApi.injectEndpoints({
  endpoints: (builder) => ({
    getMasterPage: builder.mutation<PagedResponse<MasterRow>, { path: string; searchValue: string; page: number; size: number }>({
      query: ({ path, searchValue, page, size }) => ({
        url: `/${path}/search-by-criteria/page=${page}/size=${size}`,
        method: 'POST',
        body: { searchValue },
      }),
    }),

    saveMaster: builder.mutation<MasterRow, { path: string; body: MasterRow }>({
      query: ({ path, body }) => ({ url: `/${path}`, method: 'POST', body }),
      invalidatesTags: ['Brand', 'Model', 'Product', 'EmiBank', 'Supplier', 'Customer'],
    }),

    updateMaster: builder.mutation<MasterRow, { path: string; body: MasterRow }>({
      query: ({ path, body }) => ({ url: `/${path}`, method: 'PUT', body }),
      invalidatesTags: ['Brand', 'Model', 'Product', 'EmiBank', 'Supplier', 'Customer'],
    }),

    deleteMaster: builder.mutation<void, { path: string; id: number }>({
      query: ({ path, id }) => ({ url: `/${path}/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Brand', 'Model', 'Product', 'EmiBank', 'Supplier', 'Customer'],
    }),

    checkDuplicate: builder.mutation<{ duplicate: boolean }, { path: string; body: MasterRow }>({
      query: ({ path, body }) => ({ url: `/${path}/duplicate`, method: 'POST', body }),
    }),

    /* dropdowns - Brand feeds Model, Model feeds Variant */

    getBrands: builder.query<Brand[], void>({
      query: () => '/brand',
      providesTags: ['Brand'],
    }),

    getModelsByBrand: builder.query<PhoneModel[], number>({
      query: (brandId) => `/model/brand/${brandId}`,
      providesTags: ['Model'],
    }),

    getSuppliers: builder.query<MasterRow[], void>({
      query: () => '/supplier',
      providesTags: ['Supplier'],
    }),
  }),
});

export const {
  useGetMasterPageMutation,
  useSaveMasterMutation,
  useUpdateMasterMutation,
  useDeleteMasterMutation,
  useCheckDuplicateMutation,
  useGetBrandsQuery,
  useGetModelsByBrandQuery,
  useGetSuppliersQuery,
} = masterApi;
